import { FunctionalComponent, h } from '@stencil/core'
import { JSXBase } from '@stencil/core/internal'
import { noop } from 'lodash-es'
import { css, keyframes } from 'src/lib/emotion'

import { IBookmark, isScreenShare } from '../../../../bookmarks/types'
import { INote, INoteClip } from '../../../../call/types'
import { colors } from '../../../../tailwind'
import { classes, colorClasses } from '../../../../utils/css'
import { AnnotationStore } from '../annotation'
import { CallStore, NotesStore } from '../state'

function autosize(input: HTMLTextAreaElement) {
  input.style.height = '0'
  input.style.height = input.scrollHeight + 'px'
}

export const TextArea: FunctionalComponent<JSXBase.IntrinsicElements['textarea']> = ({
  value,
  autoFocus,
  class: className = '',
  style,
  ...attrs
}) => (
  <textarea
    class={`${className} bg-transparent align-top resize-none overflow-hidden`}
    ref={(el: HTMLTextAreaElement & { textareaInitialized: boolean }) => {
      if (el && !el.textareaInitialized) {
        el.textareaInitialized = true
        if (autoFocus) {
          setTimeout(() => el.focus())
        }
        autosize(el)
      }
    }}
    onInput={(e) => autosize(e.target as HTMLTextAreaElement)}
    onFocus={(e) => autosize(e.target as HTMLTextAreaElement)}
    {...attrs}
    value={value}
    style={{ lineHeight: '1.3', ...style }}
  />
)

const loopBackground = keyframes`
0% {
  opacity: 50%;
}
100% {
  opacity: 35%;
}
`

export const darkened = css`
  animation: ${loopBackground} 0.6s infinite alternate linear;
`

export const selection = css`
  ::selection {
    color: black;
    background-color: #ffffff66;
  }
`

const grabbingHand = css`
  * {
    cursor: grabbing !important;
  }
`

export const DragButton: FunctionalComponent<
  {
    direction: 'up' | 'down'
    disabled: boolean
  } & JSXBase.IntrinsicElements['button']
> = ({ class: className = '', style, disabled, direction, onMouseDown = noop, ...attrs }) => {
  return (
    <button
      {...attrs}
      class={`
        flex items-center justify-center text-base transition transform duration-200 ${className}
        ${classes(disabled, 'w-0 h-0 overflow-visible', `w-4 h-4 rounded rotate-45`)}
      `}
      style={{ cursor: disabled ? 'default' : 'grab', ...style }}
      disabled={disabled}
      onMouseDown={(e) => {
        document.body.classList.add(grabbingHand)
        document.body.addEventListener('mouseup', () => document.body.classList.remove(grabbingHand), { once: true })
        onMouseDown(e)
      }}
    >
      {disabled && (
        <div
          class={`flex-shrink-0 text-base rounded-full ${classes(direction === 'down', 'mt-0', 'mt-3')}`}
          style={{ backgroundColor: 'inherit', width: '11px', height: '11px', marginLeft: '19px' }}
        />
      )}
      {!disabled && <ui-icon class='transform -rotate-45' icon='chevron-expand' />}
    </button>
  )
}

const contentEditable = css`
  border: 1px solid transparent;
  &[contenteditable] {
    cursor: text;
    &:hover {
      text-decoration: underline;
      cursor: pointer;
      &:before {
        text-decoration: underline;
      }
    }
    &:focus {
      padding: 5px 8px;
      border-radius: 5px;
      margin-top: -6px;
      margin-bottom: -3px;
      cursor: text;
      box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1) inset;
      background: linear-gradient(to bottom, black, ${colors['surface-base']});
      text-decoration: none;
    }
    &:empty {
      &:focus:before {
        content: 'Type to add title…';
        text-decoration: none;
      }
      &:before {
        content: 'Click to add title…';
        text-decoration-color: ${colors['surface-higher']};
        cursor: pointer;
        font-style: italic !important;
        color: ${colors['surface-higher']};
      }
    }
  }
`

export const NoteHeader: FunctionalComponent<
  {
    clip: INoteClip
    autoFocus?: boolean
    onAutoFocus?: () => void
    onDelete?: () => void
    onEdit?: (note: INote) => void
    onChangeHighlight?: (menuTargetEl: HTMLElement) => void
  } & JSXBase.IntrinsicElements['div']
> = (
  {
    clip,
    autoFocus,
    onAutoFocus,
    onDelete,
    onEdit,
    onChangeHighlight,
    clip: {
      note: { bookmark },
    },
    class: className = '',
    ...attrs
  },
  children
) => {
  const highlight = clip.note.bookmark.highlight
  const editable = !(CallStore.state.readonly || isScreenShare(clip.note.bookmark))
  const processing = clip.note.id === AnnotationStore.state.processingNote?.id
  let menuTargetEl: HTMLElement
  return (
    <div
      {...attrs}
      class={`
        cursor-default relative flex items-start text-sm self-stretch -ml-6 py-1 mt-1 pl-8 sm:pl-6
        transition duration-200
        ${colorClasses({ text: bookmark.hex_color })}
        ${className}
      `}
    >
      <ui-resizing-text
        transitionIn={autoFocus}
        class='flex-shrink-0 ml-1 sm:ml-0 font-extrabold uppercase whitespace-nowrap'
      >
        {bookmark.label}
      </ui-resizing-text>
      {highlight || editable ? (
        <span class='mx-3px -mt-3px h-0'>
          <call-detail-note-menu
            icon='three-dots'
            class={`
              flex justify-center items-center p-1 text-surface-higher hover:text-white hover:bg-surface-higher rounded-full cursor-pointer
              ${classes(processing, 'pointer-events-none text-opacity-0')}
            `}
            clip={clip}
            ref={(el) => (menuTargetEl = el)}
            allowShare={highlight}
            allowDelete={editable}
            allowDownload={highlight}
            allowAddToPlaylist={
              !!NotesStore.state.playlistActions?.createUrl && !!NotesStore.state.playlistActions?.indexUrl && highlight
            }
            allowChangeHighlightType={editable && highlight}
            processing={processing}
            onDelete={(e) => {
              e.stopImmediatePropagation()
              AnnotationStore.state.processingNote = clip.note
              onDelete?.()
            }}
            onChangeHighlightTypeClicked={() => {
              onChangeHighlight?.(menuTargetEl)
            }}
          />
        </span>
      ) : (
        <span class='w-2'></span>
      )}

      <span
        contenteditable={editable}
        ref={(el) => {
          if (el && autoFocus) {
            setTimeout(() => {
              el.focus()
              onAutoFocus?.()
            }, 300)
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Escape') {
            const input = e.target as HTMLElement
            input.blur()
            e.preventDefault()
          }
        }}
        onFocus={(event: Event) => {
          const el = event.target as HTMLSpanElement & { existingValue: string }
          el.existingValue = el.textContent
        }}
        onBlur={(event: Event) => {
          const el = event.target as HTMLSpanElement & { existingValue: string }
          if (el.existingValue === el.textContent) {
            return
          }
          if (onEdit) {
            AnnotationStore.state.processingNote = clip.note
            onEdit({ ...clip.note, text: el.textContent })
          }
        }}
        class={`
            rounded-sm font-bold text-glyph-base py-2px -mt-3px
            ${contentEditable}
          `}
        innerHTML={clip.note.text}
      ></span>
      {children}
    </div>
  )
}

export const BookmarkButton: FunctionalComponent<{ bookmark: IBookmark } & JSXBase.IntrinsicElements['button']> = ({
  bookmark: { hex_color, label, bookmark, action_item },
  class: className,
  ...attrs
}) => (
  <button
    class={`
      ${className}
      flex items-center spacing-x-2 hover:bg-surface-upper transition duration-200 select-none
      ${colorClasses({ text: hex_color })}
    `}
    {...attrs}
  >
    {bookmark ? (
      <ui-icon icon='bookmark' />
    ) : action_item ? (
      <ui-icon icon='check2-square' />
    ) : (
      <ui-icon icon='play-btn' />
    )}

    <span class='text-base font-extrabold uppercase'>{label}</span>
  </button>
)
